import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { commitPayment, paymentCancelled, getPaymentResult } from "../services/paymentService";
import { PageLoader } from "../components/loader";

const PaymentResult = () => {
  const [params] = useSearchParams();
  const { buyOrder: buyOrderFromParams } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const timerRef = useRef(null);
  const alredyProccessed = useRef(false);
  const tokenRef = useRef(sessionStorage.getItem('webpay_token_ws'));
  const tbkTokenRef = useRef(sessionStorage.getItem('webpay_tbk_token'));
  const tbkOrderRef = useRef(sessionStorage.getItem('webpay_tbk_order'));

  useEffect(() => {
    const token_ws = tokenRef.current;
    const tbk_token = tbkTokenRef.current;
    const tbk_orden_compra = tbkOrderRef.current;
    const statusFromQuery = params.get("status");
    const errorParam = params.get("error"); 
 
    if (alredyProccessed.current) return;
    alredyProccessed.current = true;

    // Timeout de seguridad
    timerRef.current = setTimeout(() => {
      if (loading) {
        setErrorType("ERROR_BACKEND");
        setLoading(false);
      }
    }, 30000);

    const limpiarTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    // 0) Si viene error en query
    if (errorParam) {
      const normalized = String(errorParam).toUpperCase();
      setErrorType(normalized === "RETURN_PAYLOAD_INVALID" ? "INVALIDO" : "ERROR_BACKEND");
      window.history.replaceState({}, "", "/payment-result");
      setLoading(false);
      limpiarTimer();
      return;
    }

    // 1) Caso ABORTADO (transición): llega TBK_* al FE
    if (tbk_token) {
      // CORREGIDO: Crear un objeto result con status ABORTED
      setResult({
        status: 'ABORTED',
        buyOrder: tbk_orden_compra || 'N/A',
        transactionDate: new Date().toISOString()
      });
      
      if (tbk_orden_compra) { 
        paymentCancelled(tbk_orden_compra).catch(() => {}); 
      }
      
      sessionStorage.removeItem('webpay_token_ws');
      sessionStorage.removeItem('webpay_tbk_token');
      sessionStorage.removeItem('webpay_tbk_order');
      window.history.replaceState({}, "", "/payment-result");
      setLoading(false);
      limpiarTimer();
      return;
    }

    // 2) Caso URL limpia: /payment-result/:buyOrder?status=...
    if (buyOrderFromParams) {
      (async () => {
        try {
          const data = await getPaymentResult(buyOrderFromParams);
          setResult({
            ...data,
            status: data.status || statusFromQuery || "UNKNOWN",
          });
        } catch (err) {
          console.error("Error obteniendo resultado por buyOrder:", err);
          setErrorType("ERROR_BACKEND");
        } finally {
          sessionStorage.removeItem('webpay_token_ws');
          sessionStorage.removeItem('webpay_tbk_token');
          sessionStorage.removeItem('webpay_tbk_order');
          setLoading(false);
          limpiarTimer();
        }
      })();
      return;
    }

    // 3) Caso TRANSICIÓN: llegó token_ws en la URL del FE
    if (token_ws) {
      const confirmarPago = async () => {
        try {
          const response = await commitPayment(token_ws);
          setResult(response);
          const clean = `/payment-result/${encodeURIComponent(response.buyOrder)}?status=${encodeURIComponent(response.status)}`;
          window.history.replaceState({}, "", clean);
        } catch (err) {
          console.error("Error al confirmar pago:", err);
          setErrorType("ERROR_BACKEND");
          window.history.replaceState({}, "", "/payment-result");
        } finally {
          sessionStorage.removeItem('webpay_token_ws');
          sessionStorage.removeItem('webpay_tbk_token');
          sessionStorage.removeItem('webpay_tbk_order');
          setLoading(false);
          limpiarTimer();
        }
      };
      confirmarPago();
      return;
    }

    // 4) Sin token ni buyOrder -> inválido
    setErrorType("INVALIDO");
    setLoading(false);
    limpiarTimer();

    return () => limpiarTimer();
  }, [params, buyOrderFromParams]);

  const formatearFecha = (fechaString) => {
    if (!fechaString) return "No disponible";
    const fecha = new Date(fechaString);
    return fecha.toLocaleDateString("es-CL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatearMonto = (monto) => new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP"
  }).format(monto);

  const obtenerTipoPago = (paymentType) => {
    const tipos = {
      VD: "Débito",
      VN: "Crédito",
      VC: "Crédito",
      SI: "Crédito",
      S2: "Crédito",
      NC: "Crédito"
    };
    return tipos[paymentType] || "No especificado";
  };

  const renderContenido = () => {
    if (loading) return <PageLoader message="Cargando transacción" />;

    const Banner = ({ color, icon, title, message, subMessage }) => (
      <div className={`border ${color.bg} text-center p-6 rounded-md mb-8`}>
        <div className="flex justify-center mb-4">
          <div className={`rounded-full ${color.circle} p-2`}>
            {icon}
          </div>
        </div>
        <span className={`text-xs font-semibold ${color.text} uppercase`}>{title}</span>
        <h2 className="text-2xl font-bold mt-2">{message}</h2>
        {subMessage && <p className="text-sm text-gray-600 mt-1">{subMessage}</p>}
      </div>
    );

    const colores = {
      aprobado: { bg: "bg-green-50 border-green-200", circle: "bg-green-100", text: "text-green-600" },
      rechazado: { bg: "bg-red-50 border-red-200", circle: "bg-red-100", text: "text-red-600" },
      cancelado: { bg: "bg-yellow-50 border-yellow-200", circle: "bg-yellow-100", text: "text-yellow-600" },
    };

    // CORREGIDO: Manejo de errores primero
    if (errorType === "INVALIDO" || errorType === "ERROR_BACKEND") {
      return (
        <div className="max-w-lg mx-auto mt-16">
          <Banner
            color={colores.rechazado}
            icon={<svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
            title="Error"
            message="Error al procesar la transacción"
            subMessage="Por favor, intente nuevamente o contacte al soporte técnico si el problema persiste."
          />
          <div className="mt-6 flex justify-center">
            <button className="bg-gray-800 text-white px-4 py-2 rounded" onClick={() => navigate("/")}>
              Volver al Inicio
            </button>
          </div>
        </div>
      );
    }

    // CORREGIDO: Verificar que result existe antes de acceder a sus propiedades
    if (!result) {
      return (
        <div className="max-w-lg mx-auto mt-16">
          <Banner
            color={colores.rechazado}
            icon={<svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
            title="Error"
            message="No se pudo obtener información de la transacción"
            subMessage="Por favor, intente nuevamente."
          />
          <div className="mt-6 flex justify-center">
            <button className="bg-gray-800 text-white px-4 py-2 rounded" onClick={() => navigate("/")}>
              Volver al Inicio
            </button>
          </div>
        </div>
      );
    }

    // Caso ABORTADO/CANCELADO
    if (result.status === 'ABORTED') {
      return (
        <div className="max-w-lg mx-auto mt-16">
          <Banner
            color={colores.cancelado}
            icon={<svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 5.5a6.5 6.5 0 11-6.5 6.5A6.5 6.5 0 0112 5.5z" /></svg>}
            title="Cancelado"
            message="Transacción Cancelada"
            subMessage="La transacción fue cancelada por el usuario o por un error en el proceso."
          />
          <div className="mt-6 flex justify-center">
            <button className="bg-gray-800 text-white px-4 py-2 rounded" onClick={() => navigate("/")}>
              Volver al Inicio
            </button>
          </div>
        </div>
      );
    }

    // Caso AUTORIZADO
    if (result.status === "AUTHORIZED") {
      return (
        <div className="max-w-2xl mx-auto mt-12">
          <Banner
            color={colores.aprobado}
            icon={<svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
            title="Aprobada"
            message="Transacción Aprobada"
            subMessage="Su pago ha sido procesado exitosamente."
          />

          <div className="border rounded-md shadow-sm p-6 bg-white">
            <h3 className="font-semibold text-lg mb-4">Detalles de la Transacción</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">ID de Transacción</p>
                <p>{result.buyOrder}</p>
              </div>
              <div>
                <p className="text-gray-500">Monto</p>
                <p className="font-bold">{formatearMonto(result.amount)}</p>
              </div>
              <div>
                <p className="text-gray-500">Tipo de Pago</p>
                <p>{obtenerTipoPago(result.paymentType)}</p>
              </div>
              {result.installmentNumber && (
                <div>
                  <p className="text-gray-500">Número de Cuotas</p>
                  <p>{result.installmentNumber}</p>
                </div>
              )}
              <div>
                <p className="text-gray-500">Tarjeta</p>
                <p>{result.card ? `****${result.card}` : "No disponible"}</p>
              </div>
              <div>
                <p className="text-gray-500">Fecha y Hora</p>
                <p>{formatearFecha(result.transactionDate)}</p>
              </div>
              <div>
                <p className="text-gray-500">Código de Autorización</p>
                <p className="text-green-600">{result.authorizationCode}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-4">
              <button className="bg-[#0488AF] text-white px-4 py-2 rounded flex items-center space-x-2" onClick={() => window.print()}>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 9V2h12v7h2a2 2 0 012 2v4a2 2 0 01-2 2h-2v5H6v-5H4a2 2 0 01-2-2v-4a2 2 0 012-2h2zm2-5v5h8V4H8z" /></svg>
                <span>Descargar Comprobante</span>
              </button>
              <button className="border border-gray-300 px-4 py-2 rounded flex items-center space-x-2" onClick={() => navigate("/")}>
                <span>Volver al Sitio</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Caso RECHAZADO o cualquier otro estado
    return (
      <div className="max-w-lg mx-auto mt-12">
        <Banner
          color={colores.rechazado}
          icon={<svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
          title="Rechazada"
          message="Transacción Rechazada"
        />

        <div className="border rounded-md shadow-sm p-6 bg-white text-sm mt-6">
          <p><b>Fecha y Hora:</b> {formatearFecha(result.transactionDate)}</p>
          <p className="mt-2 text-gray-600 text-sm">Posibles causas: Error en la tarjeta, saldo insuficiente o tarjeta no habilitada.</p>
        </div>

        <div className="mt-6 flex space-x-4">
          <button className="bg-gray-800 text-white px-4 py-2 rounded" onClick={() => navigate("/")}>Volver al Inicio</button>
        </div>
      </div>
    );
  };

  return <div className="min-h-screen bg-gray-50 py-8 px-4">{renderContenido()}</div>;
};

export default PaymentResult;